/**
 * RFC 9457 problem details, as the platform renders them — G12.
 *
 *   "Errors: RFC 9457 problem-details, with a stable machine-readable `type` and a
 *    localised `detail`."
 *
 * `bata-platform`'s `services/api/errors.py` builds every failure into one document:
 * `type` is a URN (`urn:bata:problem:<code>`) that integrators branch on and that cannot
 * change without a MAJOR bump, and `title`/`detail` are the SERVER's catalogue resolved
 * for the caller's `Accept-Language`.
 *
 * ── Why the UI does not simply render the server's prose ────────────────────────────
 *
 * Because G6 puts the front end's copy in the front end's catalogue. The server's
 * `detail` is localised, but it is localised from a different catalogue, on a different
 * release cadence, and it is written for an integrator rather than for the planner
 * looking at this screen. So:
 *
 *   * a code the UI knows resolves to a UI catalogue key — `api.problem.<code>` — which
 *     the i18n coverage gate proves exists in all three launch locales;
 *   * a code the UI does not know resolves to `api.problem.unknown`, an ICU message with
 *     a `{detail}` placeholder into which the SERVER's already-localised detail is
 *     interpolated.
 *
 * The fallback is a placeholder rather than a concatenation on purpose: G6 test b makes
 * building a sentence with `+` or a template literal a build failure, and a message whose
 * word order is fixed by JavaScript is not translatable.
 *
 * Neither path renders raw text: every message a planner sees goes through the i18n
 * runtime, and an unreachable service produces `api.problem.unreachable` rather than
 * a `TypeError: Failed to fetch`.
 */

/** RFC 9457 media type. A response carrying it is a problem document by definition. */
export const PROBLEM_MEDIA_TYPE = 'application/problem+json';

/** URN namespace of the stable machine-readable `type` (services/api/errors.py). */
export const PROBLEM_TYPE_PREFIX = 'urn:bata:problem:';

/**
 * One problem document.
 *
 * Structurally the `ProblemDocument` the spec declares per router — nine identical
 * declarations, one per FastAPI router, because each router attaches its own copy. The
 * assignability is asserted in `api.test.ts` rather than assumed, so a change to the
 * published shape fails a test instead of surfacing as `undefined` in a message.
 */
export interface ProblemDetails {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string;
  readonly code: string;
  readonly instance?: string | null;
  /** RFC 9457 permits extension members and this service uses them. */
  readonly [extension: string]: unknown;
}

/**
 * Problem codes the platform skeleton registers, from `services/api/errors.py`.
 *
 * Listed rather than derived because the OpenAPI document publishes the problem SHAPE
 * and not the code vocabulary — `type` is `string` in the schema. A code that is not
 * here still produces a translated message through `api.problem.unknown`, so the list
 * degrades rather than breaks; what it buys is a message written for a planner.
 */
export const KNOWN_PROBLEM_CODES = [
  'platform.internal_error',
  'platform.request_invalid',
  'platform.not_found',
  'platform.method_not_allowed',
  'platform.unauthorised',
  'platform.forbidden',
  'platform.idempotency_key_required',
  'platform.idempotency_key_reused',
  'platform.cursor_invalid',
  'platform.page_size_invalid',
  'platform.planning_rejected',
  // Platform-wide guards (bata-platform ADR 1017, issue 687; contract 0.85). `rate_limited` (429,
  // with `Retry-After`) is the caller's per-minute budget spent on one replica: a pause, never a
  // fault of the request, so its message says to wait and try again and does not suggest the
  // person did something wrong. `query_timed_out` (503) is a statement cancelled at the service's
  // own deadline, kept apart from `internal_error` because narrowing the perimeter is the lever.
  'platform.rate_limited',
  'platform.query_timed_out',
  // ── planning refusals ──────────────────────────────────────────────────────
  // Registered by a planning router rather than by the platform skeleton, and listed for
  // the same reason as the rest: a code that is here gets a message written for a planner
  // instead of the platform's integrator-facing `detail`.
  //
  // Both of these are REFUSALS and not failures. `proposal_already_disposed` means the
  // append-only cadence trail is working exactly as designed and somebody else acted
  // first; `override_reason_required` means the write was rejected for the reason
  // `docs/05` §7 exists to enforce. Telling a planner "the service reported a problem this
  // version does not recognise" about either is worse than saying nothing, because it
  // suggests a defect where there is a rule. ch05 had to carry a private map to avoid
  // exactly that (`refusalKey`), which is now deleted.
  'planning.exit.proposal_already_disposed',
  'planning.exit.override_reason_required',
  // A refusal of the ARGUMENT, not a failure: the residual distribution is read over a
  // consolidation POSITION run, and a consolidation run was named. The platform's own detail
  // says which run to pass instead (its `position_run_id`). Platform 0.39.
  'planning.exit.not_a_position_run',
  // §4.4's markdown refusals, and `no_feasible_path` is the ordinary answer rather than
  // the exceptional one. An option holding more units than its measured demand can clear
  // before the horizon runs out has no path to its terminal target, and saying so is the
  // useful answer -- the lever is the horizon, which is the planner's own input. Under
  // `platform.planning_rejected` alone it reads "the planning engine rejected the input as
  // inconsistent", which names a defect where there is arithmetic.
  'planning.inseason.markdown.no_feasible_path',
  'planning.inseason.markdown_inputs_unavailable',
  // READS THAT DO NOT EXIST YET, refused by the service on purpose. Both answer 404 rather
  // than an empty list, because an empty list would say something false: a season with no
  // open position, or a season in which nothing was reclassified. The reasons differ and
  // the messages keep them apart — the open-to-buy position is posed against objects
  // nothing in the estate constructs today, whereas the in-season cycle DOES re-project the
  // season and keeps the result only inside its run report. Audit v4 G-04 found both
  // rendered as "the service reported a problem this version of the application does not
  // recognise", which tells a planner to call support about a deliberate refusal.
  //
  // Listed BY CODE, never by the `_unavailable` suffix. The platform uses that suffix for
  // three different things: a capability not built (these two, 404), inputs missing for
  // this option (`markdown_inputs_unavailable`, 422) and a backing service down
  // (`backend_unavailable`, `job_store_unavailable`, 503). Matching the suffix would tell
  // a planner that an outage is a roadmap item.
  'planning.inseason.open_positions_unavailable',
  'planning.inseason.projections_unavailable',
  // No run stock-summary source is wired in the serving process (bata-platform ADR 6073): a
  // refusal rather than a summary of zeroes, which would read as a run with no stock anywhere.
  // The Stock page draws it as a designed «not published» (`features/inseason/stock-summary.ts`).
  'planning.inseason.stock_summary_unavailable',
  // No write-back summary source is wired in the serving process (bata-platform ADR 6170): a refusal
  // rather than zero commands, which would read as a write-back log with nothing in it. The Cockpit
  // draws it as «not published» (it is in the not-built list below).
  'planning.inseason.write_back_summary_unavailable',
  // DEC-111 (bata-platform `routers/postseason.py`, `ReadNotPublishedError`): the season review,
  // the accuracy warehouse, the attribute indices, the refreshes and the promotions answer 404
  // with this, naming the read in the `read` extension, while no producer persists their rows —
  // rather than the empty 200 that read as «a season with nothing to report».
  'planning.postseason.read_not_published',
  // bata-platform ADR 8012 (`routers/postseason_promotions.py`): no promotion source, or nothing
  // landed for the country — 404, never an empty 200 that reads as a market that ran no promotions;
  // and 422 when `from` is after `to` — a request defect, never an empty period.
  'planning.postseason.promotions_unavailable',
  'planning.postseason.promotions_range_invalid',
  // bata-platform ADR 8016: a window's perimeter asked for a window the register in force does not
  // hold — 404, never an empty perimeter that reads as a window over no store.
  'planning.postseason.promotion_window_not_found',
  // bata-platform ADR 8017: a store's sub-categories asked for a store the door register in force holds
  // no row for around the window — 404, never an empty list that reads as a store that sold nothing.
  'planning.postseason.promotion_window_door_not_found',
  // A REFUSAL of the run, not a failure: the run-bound Cockpit reads (bata-platform ADR 6030)
  // answer 404 with this when the run does not exist OR is outside the caller's scope, and do not
  // say which. It is not "not built" — the read exists — so it is kept out of NOT_BUILT.
  'planning.inseason.run_not_found',
  // 409 (bata-platform ADR 6060, issue 311): the run recorded no stock position, so the door-
  // availability read cannot derive its window — a statement about the run, not a fault.
  'planning.inseason.door_availability_window_unknown',
  // The transfer disposition write's refusals (bata-platform ADR 6031, platform 0.46.0). The
  // first three are REFUSALS by rule — a transfer takes one decision, a reason outside the
  // governed vocabulary is not recorded, and a transfer cannot be adjusted because its net
  // benefit is priced for its own units. The last two are shared by every in-season disposition
  // route: "you sent no reason" and "the decision store is down" (503), the latter kept apart
  // from `platform.internal_error` because a retry of the same decision is safe and says so.
  'planning.inseason.transfer_already_disposed',
  'planning.inseason.transfer_reason_not_governed',
  'planning.inseason.transfer_adjust_not_governed',
  // bata-reqs ADR 9002 stage 3: the one run's reads (platform ADR 9050), its one approval process and
  // one simulation (ADR 9049), and the 410 every retired store-to-store route answers.
  'planning.compacting.move_runs_unavailable',
  'planning.compacting.move_run_not_found',
  'planning.compacting.move_line_not_found',
  'planning.compacting.route_retired',
  'planning.compacting.move_line_reason_required',
  'planning.compacting.move_line_reason_not_governed',
  'planning.compacting.move_line_adjust_units_required',
  'planning.compacting.move_line_adjust_out_of_range',
  'planning.compacting.move_line_already_disposed',
  'planning.compacting.move_line_superseded',
  'planning.compacting.move_line_simulated',
  'planning.compacting.dispositions_unavailable',
  'planning.compacting.simulations_unavailable',
  'planning.compacting.simulation_overlay_key_refused',
  'planning.compacting.simulation_market_out_of_scope',
  'planning.compacting.simulation_capacity_exhausted',
  'planning.compacting.simulation_cycle_window',
  // Health (bata-platform ADR 9053; bata-reqs ADR 9003): no Health source in the serving process (404 —
  // the report pages draw it as «not published yet»), a market outside the caller's scope or a run, week
  // or price run that is not the market's (404, one answer for both, G3.3), and a cluster grouping asked
  // for without a department family (422: clusters are fitted per family). Every `planning.health.*`
  // code the platform registers (`services/api/routers/health.py`); `HEALTH_PROBLEM_CODES` in
  // `features/strategic/health/reports.ts` is the census the Health tests hold this list to.
  'planning.health.unavailable',
  'planning.health.not_found',
  'planning.health.cluster_needs_department_family',
  // Health ▸ Lookup's refusals (bata-platform ADR 9042, 422): a sales window with one end only, inverted or over
  // 366 days, more than 2000 distinct pairs, an empty key. The page refuses each before sending (`docs/adr/3004`).
  'planning.health.lookup_window_incomplete',
  'planning.health.lookup_window_invalid',
  'planning.health.lookup_too_many_pairs',
  'planning.health.lookup_key_empty',
  // Stage 2's rule, carried over by the one approval: a held line cannot be approved or adjusted.
  'planning.capacity_pass.line_held',
  'planning.inseason.disposition_reason_required',
  'planning.inseason.disposition_write_unavailable',
  // The launch disposition write (bata-platform ADR 5001, platform issue 515): a reason outside
  // the governed proposal-disposition vocabulary, or one the verb cannot carry (422); a line in
  // the set already decided — the trail is append-only and nothing is written (409); the decision
  // store down, where a retry of the same decision is safe (503).
  'planning.allocation.disposition_reason_not_governed',
  'planning.allocation.proposal_already_disposed',
  'planning.allocation.disposition_write_unavailable',
  // The alert disposition write (bata-platform ADR 2004, #148 step 2.1). One decision per alert
  // (409), a reason outside the governed vocabulary (422), an alert unknown or outside the
  // caller's scope — one 404 for both, G3.3 — and the foundations write store down (503).
  'planning.foundations.alert_already_disposed',
  'planning.foundations.alert_reason_not_governed',
  'planning.foundations.alert_not_found',
  'platform.foundations.write_unavailable',
  // The activity register's window (bata-platform `routers/activity.py`, ADR 9037): `to` before
  // `from`, or wider than the platform's bound (422, `maximumDays` on the document). A refusal of
  // the period, not a fault: the activity page says it with both numbers, this is the message
  // anywhere else. Signed-in production check, 2026-10-02: it read «un problema che questa versione
  // dell'applicazione non riconosce».
  'platform.foundations.activity_window_invalid',
  // The alert queue's department filter (owner ruling R-12, bata-platform ADR 2106, bata-ui ADR
  // 2105): a department with no market (422), and a market whose product class scheme is not
  // pinned or cannot be served (409) — each a refusal, never an empty queue.
  'planning.foundations.alert_department_needs_market',
  'planning.foundations.alert_department_scheme_not_pinned',
  'planning.foundations.alert_department_scheme_refused',
  // The execution loop (bata-platform ADR 6037, #148 step 2.3, owner ruling R2). A planner
  // reports `dispatched` then `acknowledged`, one step at a time; anything else is refused
  // whole, and an unknown command and another market's are one 404.
  'planning.inseason.write_back_transition_out_of_order',
  'planning.inseason.write_back_command_not_found',
  // Decisions by filter (bata-platform ADR 6103, bata-ui#166 W3-6). `selection_changed` is also
  // explained in place by the filter panel, which re-reads the preview.
  'planning.inseason.selection_changed',
  'planning.inseason.selection_empty',
  'planning.inseason.adjust_not_by_filter',
  'planning.inseason.filter_dimension_not_applicable',
  // The exit lane in the loop and the backlog (bata-platform ADR 6104/6105, bata-ui#166 W3-7/W3-8).
  'planning.exit.lane_reason_not_governed',
  // bata-platform ADR 7037: a disposition over a simulated consolidation run's lanes.
  'planning.exit.consolidation.simulation.not_executable',
  'planning.inseason.write_back_units_not_applicable',
  'planning.inseason.executed_units_invalid',
  'planning.inseason.cancellation_reason_not_governed',
  // The parameter registry's refusals (bata-platform ADR 9007, multi-axis scopes and
  // retirement; bata-ui ADR 2004). `change_refused` carries its reasons on the `refusals`
  // member, which the foundations screen lists; the seven below are one reason each, raised
  // at proposal (`scope_*`, `parameter_key_unknown`, `estate_default_required`), at approval
  // (`retire_target_absent`) or by the effective-value read (`scope_ambiguous`).
  'platform.registry.change_refused',
  'platform.registry.parameter_key_unknown',
  'platform.registry.scope_dimension_not_honoured',
  'platform.registry.scope_value_unknown',
  'platform.registry.scope_malformed',
  'platform.registry.retire_target_absent',
  'platform.registry.estate_default_required',
  'platform.registry.scope_ambiguous',
  // Raised when an approval APPLIES a parameter item (bata-platform `_apply_parameter_item`,
  // #270): a row of the same key and scope is already in force from the item's date or later
  // (`effective_date_conflict`), or starts inside the window the item would cover
  // (`effective_window_overlap`). Both 409; the document names neither the key nor the dates,
  // so the message points at the card's own items (bata-ui ADR 2008).
  'platform.registry.effective_date_conflict',
  'platform.registry.effective_window_overlap',
  // Owner ruling R-4 (bata-platform ADR 9102): the country manager approving or rejecting, under
  // the exception-rule grant, a change set that is not wholly in the `exception` family.
  'platform.registry.approval_outside_exception_family',
  // Platform issue 523 (bata-platform ADR 9023): the estate has one registry tier, so a
  // promotion has nowhere to go — 409, nothing recorded. The document carries `tierInForce`,
  // which this message does not interpolate: known codes render with no values, and the one
  // tier is already on the set's own tier path in the same row.
  'platform.registry.no_promotion_tier',
  // The discount ladders edited like any parameter (bata-platform ADR 6189, bata-ui ADR 9066): a
  // ladder version refused by a named rule (422, `reason`), a later version of the same ladder
  // (409), the other purpose's ladder starting the same day (409), a stored version id (409).
  'platform.registry.ladder_revision_invalid',
  'platform.registry.ladder_supersession_conflict',
  'platform.registry.ladder_effective_date_taken',
  'platform.registry.ladder_version_exists',
  // The process calendar (bata-platform ADR 2132, bata-ui ADR 9068): a rule broken (422, `reason`,
  // `periodId` or `row`), a started or run period changed (409), a period changed since the set was
  // proposed (409, on approval), an imported workbook unreadable (422, `reason`, `row`, `column`).
  // The calendar's own forms and the approval queue say the `reason`; these are the fallbacks.
  'platform.registry.process_calendar_invalid',
  'platform.registry.process_calendar_period_locked',
  'platform.registry.process_calendar_period_changed',
  'platform.registry.process_calendar_workbook_unreadable',
  // The run-scope library (bata-platform ADR 9035/9036, bata-ui ADR 6028): a definition or a set
  // refused by a named rule (422, `reason`, `kind`, `entryId`, `subject`), a set in conflict with
  // the library (409: an id taken, an entry in use, an entry changed since the proposal), an entry
  // the market's library does not hold (404), the library unreachable (503). The editor and the
  // proposal say the `reason`; these are the fallbacks.
  'platform.registry.run_scope_invalid',
  'platform.registry.run_scope_conflict',
  'platform.registry.run_scope_entry_unknown',
  'platform.registry.run_scope_unavailable',
  'platform.registry.run_scope_run_unknown',
  // The market's date (bata-platform PR 763): a write refused because the market has no `market.timezone`.
  'platform.registry.market_timezone_not_governed',
  // bata-platform ADR 6071 (PR 606): a sub-category name repeats under several categories, so a
  // SUB_CATEGORY scope row with no category, whose name sits under more than one, is refused —
  // 422 on the scope count and on a simulation request, nothing recorded.
  'planning.inseason.campaign.sub_category_ambiguous',
  // The monthly MAIN → ZONA_SALES transition and its withdrawal (bata-platform ADR 7026/7027,
  // bata-reqs issue 35; bata-ui ADR 7003): the reads' 404s, and the decision's refusals.
  'planning.exit.lifecycle_transition.unavailable',
  'planning.exit.lifecycle_transition.run_not_found',
  'planning.exit.lifecycle_transition.recommendation_not_found',
  'planning.exit.lifecycle_transition.already_disposed',
  'planning.exit.lifecycle_transition.reason_not_governed',
  'planning.exit.lifecycle_transition.back_dated',
  'planning.exit.lifecycle_transition.month_not_first_day',
  'planning.exit.lifecycle_transition.write_unavailable',
  'planning.exit.lifecycle_withdrawal.run_not_found',
  // The outlet exit and its transfer (bata-platform ADR 7028, issue 35 P4; bata-ui ADR 7004): no run
  // for the market and month. The pages say it as the month's state; the message is for elsewhere.
  'planning.exit.lifecycle_outlet.run_not_found',
  'planning.exit.lifecycle_outlet_transfer.run_not_found',
  // The lifecycle phase register (bata-platform ADR 7031, issue 35 P5; bata-ui ADR 7005): no inferred
  // batch in force for the market (or a market outside the caller's scope — the same 404), an article
  // neither source names, a month not on its 1st, and the read not served. The register's pages say
  // the two 404s as designed states; the messages are for any other surface that meets them.
  'planning.exit.lifecycle_phases.register_not_found',
  'planning.exit.lifecycle_phases.article_not_found',
  'planning.exit.lifecycle_phases.month_not_first_day',
  'planning.exit.lifecycle_phases.unavailable',
  // «Attività utenti» (bata-platform ADR 9029; bata-ui ADR 9059): a window inverted or longer than
  // 92 days (422, `maximumDays`), a last-access read naming no user or more than fifty (422), the
  // identity broker not configured on this tier or not answering (503, `reason` — the page draws
  // `not_configured` as a designed state), more broker events than one read walks (422, `ceiling`),
  // and the read's own store down (503). The pages say the period refusals where the period is
  // fixed; these are the messages anywhere else.
  'platform.admin.user_activity_window_invalid',
  'platform.admin.last_access_users_invalid',
  'platform.admin.identity_events_unavailable',
  'platform.admin.identity_events_too_many',
  'platform.admin.user_activity_unavailable',
  // Report triage (bata-platform#686, ADR 9063): a code that no report has, on the PATCH.
  'platform.feedback.not_found',
  // No store, or no encryption key readable (503): nothing was stored (bata-platform#686, ADR 1016).
  'platform.feedback.store_unavailable',
  // 20 reports in 24 hours (429, Retry-After); owner sign-off 2026-10-14 on its own code.
  'platform.feedback.quota_exceeded',
  // The report lifecycle (bata-platform ADR 1020, ADR 9071): a move the state machine refuses
  // (422, with what it allows), an approval by someone the estate does not name (403), and an
  // approval on content that changed since it was read (409).
  'platform.feedback.transition_refused',
  'platform.feedback.not_an_approver',
  'platform.feedback.content_changed',
  // The author's side of it: a report the rubric finds incomplete (422, with `missing`), a market
  // outside the author's reach (422), a conversation that is not theirs (422), a reply on a closed
  // report (409), an amendment once the content is frozen (409), and the replies quota (429).
  'platform.feedback.incomplete',
  'platform.feedback.market_out_of_scope',
  'platform.feedback.conversation_unknown',
  'platform.feedback.report_closed',
  'platform.feedback.content_frozen',
  'platform.feedback.message_quota_exceeded',
  // OPTIMA's request intake (bata-platform ADR 2137): the conversation is closed, expired or full
  // (409) or no longer held (404); the confirmation is used, expired or superseded (409); the draft
  // is not complete (422).
  'platform.assistant.conversation_closed',
  'platform.assistant.conversation_not_found',
  'platform.assistant.confirmation_invalid',
  'platform.assistant.draft_incomplete',
] as const;

export type KnownProblemCode = (typeof KNOWN_PROBLEM_CODES)[number];

const KNOWN = new Set<string>(KNOWN_PROBLEM_CODES);

/** The i18n namespace this module's keys live in. */
const KEY_PREFIX = 'api.problem.';

/** Key used when the transport never reached the service at all. */
export const UNREACHABLE_KEY = `${KEY_PREFIX}unreachable`;

/** Key used for a problem code this build does not know. Takes `{detail}`. */
export const UNKNOWN_KEY = `${KEY_PREFIX}unknown`;

/** Key used when a failing response carried no parseable problem document. Takes `{status}`. */
export const MALFORMED_KEY = `${KEY_PREFIX}malformed`;

/** A failed request, carrying the document the service returned. */
export class ApiProblemError extends Error {
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(`${problem.code} (${problem.status}): ${problem.detail}`);
    this.name = 'ApiProblemError';
    this.problem = problem;
  }

  get code(): string { return this.problem.code; }

  get status(): number { return this.problem.status; }
}

/** The service could not be reached, so there is no document and no status. */
export class ApiUnreachableError extends Error {
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super('The API could not be reached.');
    this.name = 'ApiUnreachableError';
    this.cause = cause;
  }
}

/** A response that failed but carried nothing RFC 9457 recognises. */
export class ApiMalformedProblemError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`The API returned ${status} with no problem document.`);
    this.name = 'ApiMalformedProblemError';
    this.status = status;
  }
}

/**
 * A file the platform answered with success but that did not arrive whole: the connection ended before its last
 * byte, or fewer rows arrived than the platform stated in `X-Bata-Row-Count` (platform ADR 6201). Never handed over
 * as the file. A malformed answer still, for a caller that only knows that class.
 */
export class ApiIncompleteFileError extends ApiMalformedProblemError {
  /** The rows that arrived, when the body did; `null` when the connection was cut. */
  readonly received: number | null;
  /** The rows the platform stated, when it stated them. */
  readonly stated: number | null;

  constructor(status: number, received: number | null = null, stated: number | null = null) {
    super(status);
    this.name = 'ApiIncompleteFileError';
    this.received = received;
    this.stated = stated;
  }
}

/** Narrowing guard: an arbitrary parsed body is an RFC 9457 document. */
export function isProblemDetails(value: unknown): value is ProblemDetails {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate['type'] === 'string'
    && typeof candidate['title'] === 'string'
    && typeof candidate['detail'] === 'string'
    && typeof candidate['code'] === 'string'
    && typeof candidate['status'] === 'number';
}

/** The stable code carried by a problem `type` URN, for a document built elsewhere. */
export function codeFromType(type: string): string {
  return type.startsWith(PROBLEM_TYPE_PREFIX) ? type.slice(PROBLEM_TYPE_PREFIX.length) : type;
}

/**
 * What to render for a failure: a catalogue key, and the ICU arguments it takes.
 *
 * Returning the key and its arguments rather than a formatted string keeps this module
 * free of a translator and free of a React dependency, which is what makes it testable
 * without a render. `useProblemMessage` in `./hooks.ts` is the one place that formats.
 */
export interface ProblemMessage {
  readonly key: string;
  readonly values: Readonly<Record<string, string | number>>;
  /** Present when the failure carried a document; absent for a transport failure. */
  readonly problem?: ProblemDetails;
}

/**
 * The narrowest code the document carries.
 *
 * `platform.planning_rejected` is a category, not a reason: the planning engine puts the
 * reason in the `planningCode` extension member, and RFC 9457 exists to let it. A screen
 * that keyed only on `code` would tell a planner "the planning engine rejected the input as
 * inconsistent" for every §4.4 refusal alike -- including `no_feasible_path`, which is not
 * an inconsistency at all but the finding that this option cannot reach its terminal target
 * in the weeks it was given.
 *
 * Falls back to `code` whenever the extension is absent, is not a string, or names
 * something this release has no message for -- so a platform that adds a planning code
 * degrades to the category message rather than to the unknown-code one.
 */
function narrowestCode(problem: ProblemDetails): string {
  const planning = problem['planningCode'];
  return typeof planning === 'string' && KNOWN.has(planning) ? planning : problem.code;
}

export function problemMessage(error: unknown): ProblemMessage {
  if (error instanceof ApiProblemError) {
    const code = narrowestCode(error.problem);
    const known = KNOWN.has(code);
    return known
      ? { key: `${KEY_PREFIX}${code}`, values: {}, problem: error.problem }
      // The server's `detail` is already localised for this request's Accept-Language,
      // so interpolating it into a UI message is not raw text — it is the one part of
      // the sentence the UI cannot know, placed by the translator, not by JavaScript.
      : { key: UNKNOWN_KEY, values: { detail: error.problem.detail }, problem: error.problem };
  }
  if (error instanceof ApiMalformedProblemError) {
    return { key: MALFORMED_KEY, values: { status: error.status } };
  }
  return { key: UNREACHABLE_KEY, values: {} };
}

/**
 * The refusals that mean "this read is not built yet" — ADR 0015's amendment, audit v4 G-04.
 *
 * BY CODE, and never by the `_unavailable` suffix: the platform uses that suffix for a read
 * not built (404), for inputs missing on this option (422) and for a backing service that is
 * down (503). Rendering an outage as "not built yet" would tell a planner that a failure they
 * should report is a roadmap item. A code joins this list only once its own detail has been
 * read and says the capability does not exist yet.
 */
export const NOT_BUILT_PROBLEM_CODES: readonly KnownProblemCode[] = [
  'planning.inseason.open_positions_unavailable',
  'planning.inseason.projections_unavailable',
  'planning.inseason.write_back_summary_unavailable',
  'planning.postseason.read_not_published',
];

const NOT_BUILT = new Set<string>(NOT_BUILT_PROBLEM_CODES);

/** Whether a failure is a deliberate "this does not exist yet" rather than a fault. */
export function isNotBuiltProblem(error: unknown): boolean {
  return error instanceof ApiProblemError
    && error.status === 404
    && NOT_BUILT.has(narrowestCode(error.problem));
}

/**
 * Whether a failure means "you are not allowed", which G2 §2.6 makes a DESIGNED state
 * distinct from an error: an empty table because a planner lacks a permission is not the
 * same screen as an empty table because the service fell over.
 */
export function isPermissionProblem(error: unknown): boolean {
  return error instanceof ApiProblemError
    && (error.status === 401 || error.status === 403);
}
